import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm, type FieldError } from "react-hook-form";
import LocationsTree from "../../../AppView/ScopeManagement/LocationsTree/LocationsTree";
import type { TreeNode } from "../../../Components/Tree/types";
import type { SelectedScope } from "../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import { resourceTypes } from "../../FindingsUtils";
import {
  TextButton,
  TextInput,
  ModalContext,
  ModalButtonsContainer,
  REQUIRED_ERROR_MESSAGE,
  AlertIndicator,
  alertTypes,
} from "@SeemplicityDev/shared";
import { AppPreferencesContext } from "../../../General/AppPreferencesProvider";
import { NetworkStatus, useQuery } from "@apollo/client";
import { SUGGEST_AI_FILTER_TITLE } from "../FindingsFilterApi";
import EnableAIIndicator from "../../../SeemAI/EnableAIIndicator";

interface FilterSaveAsFormValues {
  saveAsFilterName: string;
  saveAsLocation: TreeNode | string;
}

interface SuggestAiFilterTitleData {
  suggest_ai_filter_title?: {
    filter_title?: string | null;
  } | null;
}

interface FilterSaveAsProps {
  /**
   * Genuinely polymorphic across the four consumers, which pass mutually
   * incompatible callbacks:
   * - SCOPE edit (ScopesList): `(data) => saveScope(data, node)`
   * - Scope save-as (ScopeSettings): `(data: ScopeFormData) => void`
   * - Filter save-as (FilterPanel): RHF `UseFormSetValue`
   * - Filter rename (FiltersMenuPopup): `(prop, name) => void`
   * Declared as a method with `never` params so every consumer callback stays
   * assignable (method-syntax params are bivariant), matching the prior untyped
   * JS behavior. The two real call shapes are recovered via a cast in `onSubmit`.
   */
  setValue(field: never, value: never): void;
  type: string;
  filter_data?: string | null;
  scopesList?: TreeNode[] | null;
  location?: TreeNode | string;
  name?: string;
  selectedScope?: SelectedScope | null;
  placeholder?: string;
  buttonText?: string;
  notice?: React.ReactNode;
}

const FilterSaveAs: React.FC<FilterSaveAsProps> = ({
  setValue,
  scopesList,
  type,
  location,
  name,
  placeholder = "Filter Name",
  buttonText = "Save Filter",
  filter_data,
  notice,
}) => {
  const { closeModal } = useContext(ModalContext);
  const {
    control,
    formState: { isValid, isDirty, errors },
    handleSubmit,
    setValue: setFilterName,
  } = useForm<FilterSaveAsFormValues>({
    defaultValues: {
      saveAsFilterName: name || "",
      saveAsLocation: location || "",
    },
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    appPreferences: { useAI },
  } = useContext(AppPreferencesContext);

  const [isGeneratedByAi, setIsGeneratedByAi] = useState(false);
  // If !!name, we are in edit mode and should not overwrite the current name with AI-generated one unless the user chooses to generate it.
  const [shouldBlockAi, setShouldBlockAi] = useState(!!name);

  const { data, error, loading, refetch, networkStatus } =
    useQuery<SuggestAiFilterTitleData>(SUGGEST_AI_FILTER_TITLE, {
      fetchPolicy: "network-only",
      variables: {
        filter_type: type.toUpperCase(),
        filter_data: filter_data,
      },
      skip: !useAI || shouldBlockAi,
      notifyOnNetworkStatusChange: true,
    });

  const isLoadingAi =
    loading ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.loading;

  const onSubmit = (data: FilterSaveAsFormValues) => {
    // `setValue` is declared with `never` params (polymorphic across consumers);
    // recover the two real call shapes here without changing runtime behavior.
    const invokeSetValue = setValue as (
      arg: FilterSaveAsFormValues | "saveAsFilterName",
      value?: string
    ) => void;
    if (type === resourceTypes.SCOPE) {
      invokeSetValue(data);
    } else {
      invokeSetValue("saveAsFilterName", data?.saveAsFilterName);
    }
  };

  useEffect(() => {
    if (!!data) {
      if (shouldBlockAi) {
        return;
      }
      if (!isLoadingAi && !shouldBlockAi) {
        setFilterName(
          "saveAsFilterName",
          // Preserve the original runtime value (may be null/undefined when the
          // AI returns no title); cast to satisfy RHF's string field type.
          data?.suggest_ai_filter_title?.filter_title as string,
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        setIsGeneratedByAi(true);
      }
    }
  }, [data, shouldBlockAi, isLoadingAi]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        className="modal-content-top filter-save-as"
        data-testid={`${type.toLowerCase()}-save-as-modal`}
      >
        {notice && <AlertIndicator type={alertTypes.INFO} content={notice} />}
        {!useAI && (
          <EnableAIIndicator
            info={`Seemplicity AI can suggest a ${type} name`}
          />
        )}
        <Controller
          name="saveAsFilterName"
          render={({ field }) => {
            // The original JS destructured `onFocus` off the RHF field (always
            // undefined) and forwarded it to TextInput — preserved here.
            const { onChange, onBlur, value, ref } = field;
            const { onFocus } = field as typeof field & {
              onFocus?: () => void;
            };
            return (
              <TextInput
                onBlur={onBlur}
                onFocus={onFocus}
                label={"Name"}
                labelTop
                data-testid={`${type}-name`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                  setIsGeneratedByAi(false);
                  if (isLoadingAi) {
                    setShouldBlockAi(true);
                  }
                }}
                required
                inputRef={ref}
                placeholder={placeholder}
                value={value}
                error={
                  errors && errors.saveAsFilterName
                    ? (errors.saveAsFilterName as FieldError)
                    : null
                }
                hasAISupport={!!useAI}
                aiProps={
                  !!useAI
                    ? {
                        isGeneratedByAi,
                        onGenerateAI: () => {
                          refetch();
                          setShouldBlockAi(false);
                        },
                        loading: isLoadingAi,
                        error: error?.message,
                      }
                    : {}
                }
              />
            );
          }}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
          defaultValue={""}
        />
        {type === resourceTypes.SCOPE && (
          <Controller
            name={`saveAsLocation`}
            render={({ field: { value, onChange } }) => (
              <LocationsTree
                label={"Location"}
                required
                labelTop
                data-testid={"scope-location"}
                name={`saveAsLocation`}
                onChange={onChange}
                // The original JS referenced errors?.location, which is not a
                // field on this form and so was always undefined → null.
                error={null}
                value={value as TreeNode}
                root={scopesList}
                popoverClassName={"tree-in-scope-menu"}
              />
            )}
            control={control}
          />
        )}
        <ModalButtonsContainer>
          <TextButton
            type="button"
            label={"Cancel"}
            onClick={() => {
              closeModal();
            }}
          />
          <TextButton
            data-testid={"save-new-scope"}
            type="submit"
            label={buttonText}
            buttonStyle={"primary"}
            disabled={!isValid || !isDirty}
          />
        </ModalButtonsContainer>
      </div>
    </form>
  );
};

export default FilterSaveAs;
